// High5 — marka bazlı haftalık net satış yorumu (otomatik).
//
// Ajansın elle yazdığı haftalık mailin (yatırım · net satış · ROAS, marka durum
// tablosu, marka notları, aksiyonlar) veriden üretilen karşılığı. Girdi, "Marka
// Bazlı Haftalık Harcama · Gerçekleşen Net Satış · ROAS" matrisinin kendisi
// (buildNetSalesMatrix) → yorumdaki her rakam tablodakiyle birebir aynı.
//
// Kural: ölçülmeyen hiçbir şey olgu gibi yazılmaz (indirim etkisi, talep vb.).
// Gerileyen markada "incelenmeli" diye ÖNERİ yazılır, sebep uydurulmaz.
//
// Saf modül (React/JSX yok) — terminalden gerçek veriyle test edilebilsin diye.

const MIN_SHARE = 0.012;       // net satış veya harcama payı bu altındaysa "Diğer Markalar"
const SMALL_SHARE = 0.05;      // bunun altı küçük marka → önce momentum/verimlilik değerlendirilir
const GROWTH = 0.15;           // %15 ve üstü net satış artışı = büyüme
const ROAS_DROP = 0.25;        // ROAS %25+ düşüş …
const SALES_DROP = 0.15;       // … ve net satış %15+ düşüş = performans gerilemesi
const LOW_ROAS = 2;            // bunun altı düşük verim
const MAIN_SHARE = 0.25;       // en büyük marka ve net satış payı ≥ %25 = ana büyüme markası

export const STATUS = {
  MAIN:      { dot: '🟢', label: 'Ana Büyüme Markası',    advice: 'Kontrollü bütçe artışı ile ölçekle' },
  STRONG:    { dot: '🟢', label: 'Güçlü Performans',      advice: 'Kontrollü bütçe artışına devam et' },
  GROWTH:    { dot: '🟢', label: 'Büyüme',                advice: "Kontrollü ölçekle, ROAS'ı takip et" },
  MOMENTUM:  { dot: '🟢', label: 'Pozitif Momentum',      advice: 'Kontrollü bütçe artışıyla test et' },
  EFFICIENT: { dot: '🟡', label: 'Verimli / Düşük Hacim', advice: 'Küçük bütçe testi yap' },
  STABLE:    { dot: '⚪', label: 'Stabil',                advice: 'Mevcut bütçe seviyesinde takip et' },
  SLOWDOWN:  { dot: '🟠', label: 'Satış Gerilemesi',      advice: 'Bütçe artışını beklet, düşüşü incele' },
  DECLINE:   { dot: '🟠', label: 'Performans Gerilemesi', advice: 'Bütçeyi artırma, indirim/ürün etkisini incele' },
  LOW_ROAS:  { dot: '🔴', label: 'Düşük Verim',           advice: 'Bütçeyi artırma, kampanya ve ürün yapısını gözden geçir' },
  OTHER:     { dot: '⚪', label: 'Düşük Hacim',           advice: 'Seçici ve remarketing odaklı ilerle' },
};

/* ── bütçe planı (elle girilir) ─────────────────────────────────────────────
 * Otomatik sınıflama VERİYİ anlatır (Durum kolonu); bütçe kararı ekibin. Plan girilince
 * Öneri kolonu, marka notlarının öneri cümlesi ve aksiyonlar plana göre yazılır:
 *  - planda adı geçen marka → planın metni
 *  - planda adı geçmeyen marka → bütçe ARTIŞI önerilmez (sabit tut, takip et);
 *    gerileme/düşük verim önerileri zaten artış içermediği için aynen kalır.
 * `fromReportWeek`: bu ISO haftanın raporundan itibaren geçerli (yeni plan girilene kadar).
 * 17 Eyl 2026 (Efe): 38. hafta yalnız ASICS'te ~%15 artış; ON sabit, IS %70 korundukça ON'da
 * artışa devam; BROOKS'ta artış yok.
 */
export const BUDGET_PLAN = {
  fromReportWeek: 37,
  brands: {
    ASICS: {
      advice: 'Bütçeyi ~%15 artır',
      note: 'Bu hafta bütçe yaklaşık %15 artırılıyor.',
      action: 'ASICS: bütçeyi yaklaşık %15 artırmak.',
    },
    ON: {
      advice: 'Bu hafta bütçe sabit; IS %70 korundukça artışa devam',
      note: 'Bu hafta bütçe sabit tutuluyor; gösterim payı (IS) %70 seviyesinde korunduğu sürece artışa devam edilecek.',
      action: 'ON: bu hafta bütçeyi sabit tutmak; gösterim payı (IS) %70 seviyesinde korunduğu sürece artışa devam etmek.',
    },
    BROOKS: {
      advice: 'Bu hafta bütçe artışı yok',
      note: 'Bu hafta bütçe artışı yapılmıyor.',
      action: 'BROOKS: bu hafta bütçe artışı yapmamak.',
    },
  },
};

// Bütçe artışı öneren durumlar — planda adı geçmeyen markada "sabit tut"a çevrilir.
const INCREASE_STATUSES = new Set(['MAIN', 'STRONG', 'GROWTH', 'MOMENTUM', 'EFFICIENT']);
const FROZEN_ADVICE = 'Bu hafta bütçe sabit, performansı takip et';
const FROZEN_NOTE = {
  MAIN: (b) => `Net satıştaki payı %${nf(b.share0 * 100, 0)} ile ana büyüme markası. Bu hafta bütçe artışı planlanmıyor; performans mevcut yatırım seviyesinde takip edilecek.`,
  STRONG: () => 'Güçlü performans; bu hafta bütçe artışı planlanmıyor, artışın sürüp sürmediği takip edilecek.',
  GROWTH: () => 'Satış tarafındaki büyüme pozitif; bu hafta bütçe artışı planlanmıyor, ROAS ve net satış takip edilecek.',
  MOMENTUM: () => 'Düşük yatırım seviyesinde toplam ortalamanın üzerinde ROAS ve artan satış; bu hafta bütçe sabit, momentumun sürüp sürmediği takip edilecek.',
  EFFICIENT: () => 'Verimli ama hacmi düşük; bu hafta bütçe sabit tutuluyor.',
};

/* ── biçim ──────────────────────────────────────────────────────────────── */
const nf = (v, d) => Number(v).toLocaleString('tr-TR', { minimumFractionDigits: d, maximumFractionDigits: d });

export function fmtTL(v) {
  const n = Number(v) || 0;
  const a = Math.abs(n);
  const s = n < 0 ? '−' : '';
  if (a >= 1_000_000) return `${s}${nf(a / 1_000_000, 1)}M TL`;
  if (a >= 1_000) return `${s}${nf(a / 1_000, 0)}K TL`;
  return `${s}${nf(a, 0)} TL`;
}
export const fmtRoas = (v) => (v == null ? '—' : nf(v, 2));
const pctChange = (cur, prev) => (prev > 0 ? (cur - prev) / prev : null);
const fmtPctAbs = (p) => `%${nf(Math.abs(p) * 100, 0)}`;

/** "70K TL → 103K TL (%47 artış)" */
function move(prev, cur) {
  const p = pctChange(cur, prev);
  let tail = '';
  if (p != null) {
    if (Math.abs(p) < 0.01) tail = ' (yatay)';
    else tail = ` (${fmtPctAbs(p)} ${p > 0 ? 'artış' : 'düşüş'})`;
  }
  return `${fmtTL(prev)} → ${fmtTL(cur)}${tail}`;
}
function roasMove(prev, cur) {
  if (cur == null) return 'hesaplanamadı';
  if (prev == null) return fmtRoas(cur);
  return `${fmtRoas(prev)} → ${fmtRoas(cur)}`;
}
const joinNames = (list) => (list.length <= 1 ? list.join('') : `${list.slice(0, -1).join(', ')} ve ${list[list.length - 1]}`);

/** ISO hafta no — week anahtarı ISO Pazartesi (YYYY-MM-DD). */
export function isoWeekNo(weekStart) {
  const d = new Date(`${weekStart}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + 3);
  const jan4 = new Date(Date.UTC(d.getUTCFullYear(), 0, 4));
  const week1Mon = new Date(jan4);
  week1Mon.setUTCDate(jan4.getUTCDate() - ((jan4.getUTCDay() + 6) % 7));
  return 1 + Math.round((d - week1Mon) / (7 * 86400000));
}

/* ── sınıflama ──────────────────────────────────────────────────────────── */
function classify(b, ctx) {
  const { c0, n0, n1, r0, r1 } = b;
  const g = pctChange(n0, n1);
  if (c0 > 0 && (r0 == null || r0 < LOW_ROAS)) return 'LOW_ROAS';
  if (r1 != null && r0 != null && r0 < r1 * (1 - ROAS_DROP) && g != null && g <= -SALES_DROP) return 'DECLINE';
  if (b.key === ctx.topKey && b.share0 >= MAIN_SHARE && r0 >= ctx.totalRoas * 0.8) return 'MAIN';
  if (b.share0 < SMALL_SHARE && b.costShare0 < SMALL_SHARE && r0 >= ctx.totalRoas) return n0 > n1 ? 'MOMENTUM' : 'EFFICIENT';
  if (g != null && g >= GROWTH && r0 >= ctx.totalRoas) return 'STRONG';
  if (g != null && g >= GROWTH) return 'GROWTH';
  if (g != null && g <= -SALES_DROP) return 'SLOWDOWN';
  return 'STABLE';
}

const NOTE = {
  MAIN: (b) => `Net satıştaki payı %${nf(b.share0 * 100, 0)} ile ana büyüme markası. Kontrollü bütçe artışıyla ölçekleme fırsatı test edilebilir; ek yatırımın satış katkısı yakından izlenmeli.`,
  STRONG: (b) => (b.c0 > b.c1 * 1.02
    ? 'Ek yatırım satışa güçlü yansıyor ve ROAS toplam ortalamanın üzerinde; kontrollü ölçeklemeye devam edilebilir.'
    : 'Yatırım artmadan net satış güçlü yükseldi ve ROAS toplam ortalamanın üzerinde; kontrollü bütçe artışıyla ölçekleme test edilebilir.'),
  GROWTH: () => 'Satış tarafındaki büyüme pozitif; kontrollü bütçe artışıyla ROAS yakından takip edilmeli.',
  MOMENTUM: () => 'Düşük yatırım seviyesinde toplam ortalamanın üzerinde ROAS ve artan satış; kontrollü bütçe artışıyla ölçeklenebilirlik test edilebilir.',
  EFFICIENT: () => 'Verimli ama hacmi düşük; küçük bir bütçe testiyle potansiyeli ölçülebilir.',
  STABLE: () => 'Performans stabil; mevcut bütçe seviyesinde takip edilebilir.',
  SLOWDOWN: () => 'Net satış belirgin geriledi; bütçe artışı beklemeye alınıp düşüşün kaynağı incelenmeli.',
  DECLINE: () => 'Bütçe artırılmadan önce indirimli ürün oranı, ürün performansı ve paid görünürlük birlikte incelenerek düşüşün kaynağı belirlenmeli.',
  LOW_ROAS: () => 'ROAS toplam ortalamanın belirgin altında; bütçe artırılmamalı, kampanya ve ürün yapısı gözden geçirilmeli.',
};

/* ── ana fonksiyon ──────────────────────────────────────────────────────── */
/**
 * @param {{weekList: string[], matrix: object[], totalsRow: object}} m — buildNetSalesMatrix çıktısı
 *        (weekList en yeni hafta başta).
 * @returns null (yetersiz veri) | yorum nesnesi
 */
export function buildNetSalesCommentary({ weekList, matrix, totalsRow }, plan = BUDGET_PLAN) {
  if (!weekList || weekList.length < 2 || !totalsRow) return null;
  const T = totalsRow.cells;
  const c0 = T[0].cost, c1 = T[1].cost, n0 = T[0].netSales, n1 = T[1].netSales;
  // Harcama henüz gelmediyse (canlı API yükleniyor/hata) yorum üretme — ROAS'sız yorum yanıltır.
  if (!(c0 > 0) || !(c1 > 0)) return null;

  const w0 = isoWeekNo(weekList[0]);
  const w1 = isoWeekNo(weekList[1]);
  const totalRoas = n0 / c0;
  const prevRoas = n1 / c1;
  const nWeeks = weekList.length;

  // Tüm markalar (Diğer/eşleşmeyen hariç)
  const brands = matrix.filter((r) => r.key !== 'Other').map((r) => {
    const [a, b] = r.cells;
    return {
      key: r.key,
      label: r.label,
      c0: a.cost, c1: b.cost, n0: a.netSales, n1: b.netSales, r0: a.roas, r1: b.roas,
      share0: n0 > 0 ? a.netSales / n0 : 0,
      costShare0: c0 > 0 ? a.cost / c0 : 0,
    };
  });
  const topKey = [...brands].sort((x, y) => y.n0 - x.n0)[0]?.key;
  const ctx = { totalRoas, topKey };

  const main = brands
    .filter((b) => b.share0 >= MIN_SHARE || b.costShare0 >= MIN_SHARE)
    .map((b) => ({ ...b, status: classify(b, ctx) }))
    .sort((x, y) => y.n0 - x.n0);
  const activePlan = plan && isoWeekNo(weekList[0]) >= plan.fromReportWeek ? plan : null;
  for (const b of main) {
    b.plan = activePlan?.brands?.[b.key] || null;
    b.frozen = !!activePlan && !b.plan && INCREASE_STATUSES.has(b.status);
  }
  const mainKeys = new Set(main.map((b) => b.key));
  const others = brands.filter((b) => !mainKeys.has(b.key) && (b.c0 > 0 || b.n0 !== 0));

  // ── 1) Özet
  const verb = (p) => (p == null || Math.abs(p) < 0.01 ? 'yatay seyretti' : p > 0 ? 'yükseldi' : 'geriledi');
  const summary =
    `${w1}. haftadan ${w0}. haftaya toplam yatırım ${move(c1, c0)}, net satış ${move(n1, n0)} oldu. ` +
    `Toplam ROAS ${roasMove(prevRoas, totalRoas)} ${verb(pctChange(totalRoas, prevRoas))}.`;

  // ── 2) N haftalık trend: ilk yarı ↔ son yarı haftalık ortalama
  let trend = null;
  if (nWeeks >= 4) {
    const half = Math.floor(nWeeks / 2);
    // T[0] en yeni → son yarı = 0..half-1, ilk yarı = nWeeks-half..nWeeks-1
    const avg = (from, to, k) => T.slice(from, to).reduce((s, c) => s + c[k], 0) / (to - from);
    const cRecent = avg(0, half, 'cost'), cOld = avg(nWeeks - half, nWeeks, 'cost');
    const nRecent = avg(0, half, 'netSales'), nOld = avg(nWeeks - half, nWeeks, 'netSales');
    const cp = pctChange(cRecent, cOld), np = pctChange(nRecent, nOld);
    let lead;
    if (cp >= 0.02 && np >= 0.02) lead = 'yatırım seviyesindeki artışla birlikte satış hacmi de büyüyor';
    else if (cp >= 0.02) lead = 'yatırım artmasına rağmen satış hacmi aynı ölçüde büyümüyor';
    else if (np >= 0.02) lead = 'yatırım artmadan satış hacmi büyüyor; verimlilik iyileşiyor';
    else if (cp <= -0.02 && np <= -0.02) lead = 'yatırım ve satış hacmi birlikte geriliyor';
    else lead = 'yatırım ve satış hacmi büyük ölçüde yatay seyrediyor';
    trend =
      `Son ${nWeeks} haftalık trende baktığımızda ${lead}: son ${half} haftanın haftalık ortalaması ` +
      `yatırımda ${move(cOld, cRecent)}, net satışta ${move(nOld, nRecent)} (önceki ${half} haftaya göre).`;
  }

  // ── 3) Son haftada ROAS'ın markalar arasında ayrışması
  const up = main.filter((b) => b.r0 != null && b.r1 != null && b.r0 > b.r1 * 1.02).map((b) => b.label);
  const down = main.filter((b) => b.r0 != null && b.r1 != null && b.r0 < b.r1 * 0.98).map((b) => b.label);
  let divergence = null;
  if (up.length && down.length) {
    divergence =
      `Son haftada ek yatırımın satışa katkısı markalar arasında farklılaşıyor: ROAS'ı artan ${joinNames(up)}, ` +
      `gerileyen ${joinNames(down)}. Bu nedenle bütçe dağılımı büyüme potansiyeli ve yatırım verimliliği birlikte değerlendirilerek şekillendirilmeli.`;
  } else if (up.length) {
    divergence = `Son haftada öne çıkan markaların tamamında ROAS yükseldi (${joinNames(up)}).`;
  } else if (down.length) {
    divergence = `Son haftada öne çıkan markaların tamamında ROAS geriledi (${joinNames(down)}); bütçe artışları temkinli yapılmalı.`;
  }

  // ── 4) Durum tablosu
  const table = main.map((b) => ({
    key: b.key, brand: b.label, status: b.status, ...STATUS[b.status],
    ...(b.plan ? { advice: b.plan.advice } : b.frozen ? { advice: FROZEN_ADVICE } : {}),
  }));
  if (others.length) {
    table.push({ key: '__others__', brand: 'Diğer Markalar', status: 'OTHER', ...STATUS.OTHER });
  }

  // ── 5) Marka notları
  const notes = main.map((b) => ({
    key: b.key,
    brand: b.label,
    text:
      `${w1}. haftaya göre yatırım ${move(b.c1, b.c0)}, net satış ${move(b.n1, b.n0)}; ` +
      `ROAS ${roasMove(b.r1, b.r0)}. ` +
      (b.plan ? b.plan.note : b.frozen ? FROZEN_NOTE[b.status](b) : NOTE[b.status](b)),
  }));
  if (others.length) {
    const oc = others.reduce((s, b) => s + b.c0, 0);
    const on = others.reduce((s, b) => s + b.n0, 0);
    const named = [...others].sort((x, y) => y.n0 - x.n0).map((b) => b.label);
    const list = named.length > 5 ? `${named.slice(0, 5).join(', ')} ve ${named.length - 5} marka daha` : joinNames(named);
    notes.push({
      key: '__others__',
      brand: 'Diğer Markalar',
      text:
        `${list} birlikte ${w0}. haftada ${fmtTL(oc)} yatırımla ${fmtTL(on)} net satış getirdi ` +
        `(net satıştaki payı %${nf(n0 > 0 ? (on / n0) * 100 : 0, 1)}). Hacim düşük; seçici ve remarketing odaklı ilerlenmeli.`,
    });
  }

  // ── 6) Aksiyonlar (duruma göre gruplu; plan varsa önce planlı markalar)
  const actions = [];
  const auto = main.filter((b) => !b.plan && !b.frozen);
  const by = (...st) => auto.filter((b) => st.includes(b.status)).map((b) => b.label);
  // Plan sırası (plan nesnesindeki yazım sırası) korunur — en önemli karar en üstte.
  for (const k of Object.keys(activePlan?.brands || {})) {
    if (main.some((m) => m.key === k)) actions.push(activePlan.brands[k].action);
  }
  const scale = by('STRONG', 'GROWTH');
  if (scale.length) actions.push(`${joinNames(scale)}: kontrollü bütçe artışıyla ölçekleme fırsatlarını değerlendirmek.`);
  const mainB = by('MAIN');
  if (mainB.length) actions.push(`${joinNames(mainB)}: yüksek satış hacmi nedeniyle kontrollü bütçe artışıyla ölçekleme fırsatını test etmek.`);
  const mom = by('MOMENTUM');
  if (mom.length) actions.push(`${joinNames(mom)}: kontrollü bütçe artışıyla ölçeklenebilirliği test etmek.`);
  const frozen = main.filter((b) => b.frozen).map((b) => b.label);
  if (frozen.length) actions.push(`${joinNames(frozen)}: bu hafta bütçeyi sabit tutup net satış ve ROAS'taki seyrin sürüp sürmediğini takip etmek.`);
  const dec = by('DECLINE');
  if (dec.length) actions.push(`${joinNames(dec)}: indirimli ürün oranı, ürün performansı ve paid görünürlüğü birlikte değerlendirerek performans düşüşünün kaynağını analiz etmek.`);
  const slow = by('SLOWDOWN');
  if (slow.length) actions.push(`${joinNames(slow)}: bütçe artışını bekletip net satıştaki düşüşün kaynağını incelemek.`);
  const low = by('LOW_ROAS');
  if (low.length) actions.push(`${joinNames(low)}: bütçeyi artırmadan kampanya ve ürün yapısını gözden geçirmek.`);
  const eff = by('EFFICIENT');
  const tail = [...eff, ...(others.length ? [eff.length ? 'düşük hacimli markalar' : 'Düşük hacimli markalar'] : [])];
  if (tail.length) {
    actions.push(activePlan
      ? `${joinNames(tail)}: mevcut bütçeyle remarketing odaklı ilerleyip verimliliği takip etmek.`
      : `${joinNames(tail)}: kontrollü testler ve remarketing odaklı yatırımlarla verimliliği takip etmek.`);
  }
  const stable = by('STABLE');
  if (stable.length) actions.push(`${joinNames(stable)}: mevcut bütçe seviyesinde performansı takip etmek.`);

  return {
    week: w0, prevWeek: w1, weekStart: weekList[0], weeks: nWeeks,
    totals: { c0, c1, n0, n1, roas0: totalRoas, roas1: prevRoas },
    summary, trend, divergence, table, notes, actions,
    planActive: !!activePlan,
  };
}

/** Maile yapıştırılabilir düz metin. */
export function commentaryToText(c) {
  if (!c) return '';
  const lines = [];
  lines.push(`Son ${c.weeks} haftalık marka performansını yatırım, net satış ve ROAS metrikleri üzerinden inceledik.`, '');
  lines.push(c.summary, '');
  if (c.trend) lines.push(c.trend, '');
  if (c.divergence) lines.push(c.divergence, '');
  lines.push('Öne Çıkan Değerlendirmeler', '');
  lines.push('Marka\tDurum\tÖneri');
  for (const r of c.table) lines.push(`${r.brand}\t${r.dot} ${r.label}\t${r.advice}`);
  lines.push('');
  for (const n of c.notes) lines.push(`• ${n.brand}: ${n.text}`);
  lines.push('', 'Bu Hafta Planlanan Aksiyonlar', '');
  for (const a of c.actions) lines.push(`• ${a}`);
  return lines.join('\n');
}
